import React, { useEffect, useState } from "react";
import Button from "components/ui/Button";
import Modal from "components/ui/Modal";
import GroupChatPanel from "components/commsLog/GroupChatPanel";
import MessageBlocks from "components/commsLog/MessageBlocks";
import MessageChanges from "components/commsLog/MessageChanges";
import MessageEditor from "components/commsLog/MessageEditor";
import StateBadge from "components/commsLog/StateBadge";
import {
  useApproveCommsMessage,
  useRecallCommsMessage,
  useResendCommsMessage,
  useRetractCommsMessage,
  useSaveCommsMessage,
  useStopCommsMessage,
} from "apis/commsLog";
import { editableValues } from "utils/commsBlocks";
import {
  adminActions,
  commsStates,
  formatSydney,
  skipReasons,
} from "utils/commsLogStates";

const Field = ({ label, children }) => (
  <div>
    <p className="text-xs uppercase tracking-wide text-darkgray">{label}</p>
    <div className="text-sm">{children}</div>
  </div>
);

const MessageDetail = ({ row, onClose }) => {
  // Every action here lands in a live vendor group, so none of them fire on one click.
  const [pendingAction, setPendingAction] = useState(null);
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState("");
  const [blockDraft, setBlockDraft] = useState([]);
  // `row` is the snapshot the list held when it was opened; a save moves on from it.
  const [current, setCurrent] = useState(row);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    setCurrent(row);
    setEditing(false);
    setSavedAt(null);
  }, [row]);
  const stop = useStopCommsMessage();
  const save = useSaveCommsMessage();
  const approve = useApproveCommsMessage();
  const recall = useRecallCommsMessage();
  const resend = useResendCommsMessage();
  const retract = useRetractCommsMessage();

  if (!row || !current) return null;

  const blocks = current.blocks || [];
  const storedValues = editableValues(blocks);
  const editsBlocks = storedValues.length > 0;
  // Opening the editor is not an edit; only a changed word is.
  const edited = Boolean(
    editing &&
      (editsBlocks
        ? blockDraft.some((value, index) => value !== storedValues[index])
        : text.trim() && text !== current.message),
  );

  const target = { campaignId: current.campaignId, messageId: current.messageId };
  // Saving, approving and sending carry the edit; stop, take back and retract never do.
  const editPayload = {
    ...target,
    ...(edited && editsBlocks ? { textBlocks: blockDraft } : {}),
    ...(edited && !editsBlocks ? { text } : {}),
  };

  const startEditing = () => {
    setText(current.message || "");
    setBlockDraft(storedValues);
    setSavedAt(null);
    setEditing(true);
  };

  const runSave = () =>
    save.mutate(editPayload, {
      onSuccess: (response) => {
        const stored = response?.data?.data;
        if (!response?.data?.success || !stored) return;
        setCurrent((previous) => ({
          ...previous,
          message: stored.message,
          blocks: stored.blocks || [],
        }));
        setEditing(false);
        setSavedAt(new Date());
      },
    });
  const busy =
    stop.isPending ||
    save.isPending ||
    approve.isPending ||
    recall.isPending ||
    resend.isPending ||
    retract.isPending;
  const wasDelivered = current.state === "sent";
  const canStop = !["sent", "stopped", "rejected"].includes(current.state);
  const canRetract = wasDelivered && Boolean(current.delivery?.chatMessageId);
  // Beleef holds this organisation's messages, so this one is still ours to decide.
  const canApprove = !wasDelivered && current.adminApproval?.status === "pending";
  const canRecall = !wasDelivered && current.adminApproval?.status === "approved";
  const skipsAReview = ["awaitingAdmin", "awaitingAgent", "scheduled"].includes(
    current.state,
  );

  const recipients = current.address
    ? `the vendors at ${current.address}`
    : "the vendors";

  const confirmations = {
    stop: {
      body: `This message will not be sent to ${recipients}. The agent is not told either way.`,
      confirmLabel: "Yes, stop it",
      mutation: stop,
      danger: false,
    },
    send: {
      body: `${
        wasDelivered
          ? `${recipients} already have this message. Sending it again puts a second copy in their group and notifies them again.`
          : `This goes into the live vendor group now and ${recipients} are notified.${
              skipsAReview
                ? " It has not been through the review it was waiting on."
                : ""
            }`
      }${edited ? " Your edit goes out with it." : ""}`,
      confirmLabel: wasDelivered ? "Yes, send it again" : "Yes, send it now",
      edited,
      mutation: resend,
      danger: false,
    },
    approve: {
      body: `${
        current.needsAgentApproval
          ? `This leaves your queue and appears in ${
              current.agentName || "the agent"
            }'s approvals. Nothing reaches ${recipients} until the agent sends it.`
          : `This leaves your queue and ${
              current.agentName || "the agent"
            } is told. It sends to ${recipients} in about 30 minutes unless the agent stops it.`
      }${edited ? " Your edit goes with it." : ""}`,
      confirmLabel: "Yes, send it to the agent",
      edited,
      mutation: approve,
      danger: false,
    },
    recall: {
      body: `This leaves ${
        current.agentName || "the agent"
      }'s dashboard and waits on a Beleef admin again. ${recipients} have not seen it, so nothing is undone in their group.`,
      confirmLabel: "Yes, take it back",
      mutation: recall,
      danger: false,
    },
    retract: {
      body: `${recipients} already have this message and were notified when it arrived. Retracting only replaces it with "This message was deleted" in their group.`,
      confirmLabel: "Yes, retract it",
      mutation: retract,
      danger: true,
    },
    // Only reachable once delivered; before that saving changes nothing anyone
    // has read and goes straight through.
    save: {
      body: `${recipients} already have this message. Saving replaces the words in their group and marks it edited. They are not notified again, and nothing is sent.`,
      confirmLabel: "Yes, save it",
      edited,
      mutation: save,
      danger: false,
    },
  };

  const confirming = pendingAction ? confirmations[pendingAction] : null;

  // Only the two that put words somewhere new carry the edit.
  const carriesEdit = ["send", "approve"].includes(pendingAction);

  // Saving keeps the screen open on what is now stored; everything else leaves.
  const runConfirmed = () => {
    if (pendingAction === "save") {
      setPendingAction(null);
      return runSave();
    }
    return confirming.mutation.mutate(carriesEdit ? editPayload : target, {
      onSuccess: onClose,
    });
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={current.address || "Vendor message"}
      className="max-w-6xl"
    >
      <div
        className={`grid grid-cols-1 gap-6 ${editing ? "" : "lg:grid-cols-5"}`}
      >
        <div className={`space-y-4 ${editing ? "" : "lg:col-span-3"}`}>
          {editing ? (
            <p className="text-sm font-semibold">Editing what the vendors read</p>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <StateBadge state={current.state} />
                <span className="text-xs text-darkgray">
                  {commsStates[current.state]?.hint}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Field label="Template">
                  {current.templateLabel || current.templateName || "—"}
                </Field>
                <Field label="Agent">{current.agentName || "—"}</Field>
                <Field label="Organisation">{current.organizationName || "—"}</Field>
                <Field label="Created">{formatSydney(current.createdAt)}</Field>
                <Field label="Scheduled">{formatSydney(current.scheduled)}</Field>
                <Field label="Sent">{formatSydney(current.sentAt)}</Field>
              </div>

              {current.skipReason ? (
                <div className="rounded-md bg-lightgray p-3 text-sm">
                  {skipReasons[current.skipReason] || current.skipReason}
                </div>
              ) : null}

              {/* What dev ops asked for: the reason the send did not land. */}
              {current.delivery?.state === "failed" ? (
                <div className="rounded-md bg-red-50 p-3">
                  <p className="text-xs uppercase tracking-wide text-red-700">
                    Delivery error
                  </p>
                  <p className="text-sm text-red-700">{current.delivery.error}</p>
                  <p className="mt-1 text-xs text-red-700">
                    Attempted {formatSydney(current.delivery.attemptedAt)}
                  </p>
                </div>
              ) : null}


              {!current.delivery && wasDelivered ? (
                <p className="rounded-md bg-lightgray p-3 text-xs text-darkergray">
                  Sent before delivery was recorded, so this one is not verified. It
                  cannot be retracted.
                </p>
              ) : null}

              {current.adminAction?.action ? (
                <p className="text-xs text-darkgray">
                  A Beleef admin{" "}
                  {adminActions[current.adminAction.action] || current.adminAction.action} this on{" "}
                  {formatSydney(current.adminAction.at)}.
                </p>
              ) : null}
            </>
          )}

          {editing ? (
            <MessageEditor
              blocks={blocks}
              text={text}
              values={blockDraft}
              onChangeText={setText}
              onChangeValues={setBlockDraft}
            />
          ) : null}

          {savedAt && !editing ? (
            <p className="rounded-md bg-green-50 p-3 text-sm text-green-800">
              Saved at {formatSydney(savedAt)}. This is what is stored now, and what the
              send below will carry.
            </p>
          ) : null}

          {editing ? null : (
            <Field label={wasDelivered ? "What the vendors saw" : "What the vendors will see"}>
              {current.blocks?.length ? (
                <div className="mt-1">
                  <MessageBlocks blocks={current.blocks} />
                </div>
              ) : (
                <pre className="mt-1 whitespace-pre-wrap rounded-md border border-mediumgray p-3 text-sm">
                  {current.deliveredText || current.message || "(no text)"}
                </pre>
              )}
            </Field>
          )}

          {editing ? null : (
            <MessageChanges
              contextCheck={current.contextCheck}
              originalMessage={current.originalMessage}
              currentMessage={current.message}
            />
          )}

          {confirming ? (
            <div
              className={`rounded-md border p-3 ${
                confirming.danger
                  ? "border-red-200 bg-red-50"
                  : "border-mediumgray bg-lightgray"
              }`}
            >
              <p
                className={`text-sm ${
                  confirming.danger ? "text-red-700" : "text-darkergray"
                }`}
              >
                {confirming.body}
              </p>
              <div className="mt-3 flex gap-2">
                <Button
                  className={`rounded-md px-3 py-1.5 text-sm text-white ${
                    confirming.danger ? "bg-red-600" : "bg-black"
                  }`}
                  loading={confirming.mutation.isPending}
                  disabled={busy}
                  onClick={runConfirmed}
                >
                  {confirming.confirmLabel}
                </Button>
                <Button
                  className="rounded-md border border-mediumgray px-3 py-1.5 text-sm"
                  disabled={busy}
                  onClick={() => setPendingAction(null)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2 border-t border-mediumgray pt-4">
              {canStop ? (
                <Button
                  className="rounded-md bg-black px-4 py-2 text-sm text-white"
                  disabled={busy}
                  onClick={() => setPendingAction("stop")}
                >
                  Stop
                </Button>
              ) : null}

              {current.message ? (
                <Button
                  className="rounded-md border border-mediumgray px-4 py-2 text-sm"
                  disabled={busy}
                  onClick={() => (editing ? setEditing(false) : startEditing())}
                >
                  {editing ? "Discard the edit" : "Edit"}
                </Button>
              ) : null}

              {/* Keeping the edit is its own step; nothing leaves this screen.
                  Once delivered it also rewrites the vendors' own copy, so that
                  one asks first. */}
              {editing ? (
                <Button
                  className="rounded-md border border-black px-4 py-2 text-sm"
                  loading={save.isPending}
                  disabled={busy || !edited}
                  onClick={() =>
                    wasDelivered ? setPendingAction("save") : runSave()
                  }
                >
                  {wasDelivered ? "Save and update their copy" : "Save the edit"}
                </Button>
              ) : null}

              {canApprove ? (
                <Button
                  className="rounded-md border border-black px-4 py-2 text-sm"
                  disabled={busy}
                  onClick={() => setPendingAction("approve")}
                >
                  Send to the agent
                </Button>
              ) : null}

              {canRecall ? (
                <Button
                  className="rounded-md border border-mediumgray px-4 py-2 text-sm"
                  disabled={busy}
                  onClick={() => setPendingAction("recall")}
                >
                  Take it back from the agent
                </Button>
              ) : null}

              <Button
                className="rounded-md border border-black px-4 py-2 text-sm"
                disabled={busy || !current.message}
                onClick={() => setPendingAction("send")}
              >
                {wasDelivered ? "Send again" : "Send to the vendors now"}
              </Button>

              {canRetract ? (
                <Button
                  className="rounded-md border border-red-600 px-4 py-2 text-sm text-red-600"
                  disabled={busy}
                  onClick={() => setPendingAction("retract")}
                >
                  Retract
                </Button>
              ) : null}
            </div>
          )}
        </div>

        {editing ? null : (
          <div className="lg:col-span-2 lg:border-l lg:border-mediumgray lg:pl-6">
            <p className="mb-2 text-xs uppercase tracking-wide text-darkgray">
              Vendor group chat
            </p>
            <GroupChatPanel propertyId={current.propertyId} />
          </div>
        )}
      </div>
    </Modal>
  );
};

export default MessageDetail;
